/* UPVCSTORE APECS keyed alike set builder (sections/upvc-apecs-ka-builder.liquid).
 * Pick type, finish and size, add cylinders to a set, then add the whole set
 * (cylinders, keyed alike charges and extra keys) to the basket in one
 * request. Each line carries a "Keyed alike set" reference. The unfinished
 * set is kept in localStorage as a convenience only.
 */
(() => {
  const STORAGE_KEY = 'upvc-apecs-ka-set-v1';
  const MAX_ITEMS = 30;
  const MAX_KEYS = 50;
  const REF_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const SWATCHES = {
    nickel: 'linear-gradient(135deg, #e4e5e3 0%, #b9bbb9 55%, #d6d7d5 100%)',
    brass: 'linear-gradient(135deg, #e3c27a 0%, #b38a3e 55%, #d5b064 100%)',
    black: 'linear-gradient(135deg, #3a3b3e 0%, #121214 100%)',
  };

  const store = {
    read() {
      try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      } catch (error) {
        return null;
      }
    },
    write(value) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      } catch (error) {
        /* storage unavailable: the set simply is not remembered */
      }
    },
    clear() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        /* ignore */
      }
    },
  };

  const clamp = (value, min, max) => {
    const number = parseInt(value, 10);
    if (Number.isNaN(number)) return min;
    return Math.min(Math.max(number, min), max);
  };

  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const finishKey = (finish) => {
    const value = (finish || '').toLowerCase();
    if (value.includes('black')) return 'black';
    if (value.includes('brass') || value.includes('gold')) return 'brass';
    if (value.includes('nickel') || value.includes('chrome') || value.includes('satin')) return 'nickel';
    return null;
  };

  const newRef = () => {
    let ref = 'KA-';
    const random = window.crypto && window.crypto.getRandomValues
      ? window.crypto.getRandomValues(new Uint32Array(4))
      : [0, 0, 0, 0].map(() => Math.floor(Math.random() * 1e9));
    for (let i = 0; i < 4; i += 1) ref += REF_CHARS[random[i] % REF_CHARS.length];
    return ref;
  };

  const prepare = (cylinder) => {
    const names = cylinder.options.map((name) => name.toLowerCase());
    const finishIdx = names.findIndex((name) => /colou?r|finish/.test(name));
    const internalIdx = names.indexOf('internal size');
    const externalIdx = names.indexOf('external size');
    const sizeIdx = names.indexOf('size');
    // Read both catalogue formats during the migration. Never reverse the ordered pair.
    const variants = cylinder.variants.map((variant) => {
      const legacy = sizeIdx >= 0 ? String(variant.options[sizeIdx]).match(/\d+/g) : null;
      return {
        ...variant,
        internal: internalIdx >= 0 ? variant.options[internalIdx] : legacy && `${legacy[0]}mm`,
        external: externalIdx >= 0 ? variant.options[externalIdx] : legacy && `${legacy[1]}mm`,
      };
    });
    const unique = (index) =>
      index < 0 ? [] : [...new Set(cylinder.variants.map((variant) => variant.options[index]))];
    const prices = cylinder.variants.map((variant) => variant.price);
    return {
      ...cylinder,
      variants,
      finishIdx,
      finishes: unique(finishIdx),
      internalSizes: [...new Set(variants.map((variant) => variant.internal).filter(Boolean))].sort((a, b) => parseInt(a, 10) - parseInt(b, 10)),
      externalSizes: [...new Set(variants.map((variant) => variant.external).filter(Boolean))].sort((a, b) => parseInt(a, 10) - parseInt(b, 10)),
      thumbturn: /thumbturn|thumb/i.test(cylinder.label),
      minPrice: Math.min(...prices),
    };
  };

  const init = (root) => {
    if (root.dataset.kaReady) return;
    let data;
    try {
      data = JSON.parse(root.querySelector('[data-ka-data]').textContent);
    } catch (error) {
      return;
    }
    if (!data.cylinders || !data.cylinders.length) return;
    root.dataset.kaReady = 'true';

    const q = (selector) => root.querySelector(selector);
    let format;
    try {
      format = new Intl.NumberFormat('en-GB', { style: 'currency', currency: data.currency || 'GBP' });
    } catch (error) {
      format = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    }
    const money = (cents) => format.format(cents / 100);

    const cylinders = new Map(data.cylinders.map((cylinder) => [cylinder.block, prepare(cylinder)]));
    const findVariant = (cylinder, finish, internal, external) =>
      cylinder.variants.find(
        (variant) =>
          (cylinder.finishIdx < 0 || variant.options[cylinder.finishIdx] === finish) &&
          variant.internal === internal && variant.external === external,
      );
    const variantById = (cylinder, id) => cylinder.variants.find((variant) => variant.id === id);
    const artFor = (cylinder, finish) => {
      const key = finishKey(finish);
      return (cylinder.art && key && cylinder.art[key]) || cylinder.image;
    };
    const describe = (cylinder, variant) => {
      const parts = [];
      parts.push(`Internal ${variant.internal}`, `External ${variant.external}`);
      if (cylinder.finishIdx >= 0) parts.push(variant.options[cylinder.finishIdx]);
      return parts.join(' · ');
    };

    const els = {
      fallback: q('[data-ka-fallback]'),
      app: q('[data-ka-app]'),
      types: [...root.querySelectorAll('[data-ka-type]')],
      finishStep: q('[data-ka-finish-step]'),
      finishes: q('[data-ka-finishes]'),
      sizeStep: q('[data-ka-size-step]'),
      sizes: q('[data-ka-sizes]'),
      door: q('[data-ka-door]'),
      previewImage: q('[data-ka-preview-image]'),
      previewTitle: q('[data-ka-preview-title]'),
      previewDetail: q('[data-ka-preview-detail]'),
      previewPrice: q('[data-ka-preview-price]'),
      add: q('[data-ka-add]'),
      addStatus: q('[data-ka-add-status]'),
      empty: q('[data-ka-empty]'),
      list: q('[data-ka-list]'),
      summary: q('[data-ka-summary]'),
      cylLabel: q('[data-ka-sum-cyl-label]'),
      cylTotal: q('[data-ka-sum-cyl]'),
      chargeLabel: q('[data-ka-sum-charge-label]'),
      chargeNote: q('[data-ka-sum-charge-note]'),
      chargeTotal: q('[data-ka-sum-charge]'),
      keysRow: q('[data-ka-keys-row]'),
      keys: q('[data-ka-keys]'),
      keyPrice: q('[data-ka-key-price]'),
      total: q('[data-ka-sum-total]'),
      submit: q('[data-ka-submit]'),
      hint: q('[data-ka-hint]'),
      submitStatus: q('[data-ka-submit-status]'),
      bar: q('[data-ka-bar]'),
      barText: q('[data-ka-bar-text]'),
      count: q('[data-ka-count]'),
      countCyl: q('[data-ka-count-cyl]'),
      countCylWord: q('[data-ka-count-cyl-word]'),
      countCylIcons: q('[data-ka-count-cyl-icons]'),
      countKeys: q('[data-ka-count-keys]'),
      countKeysWord: q('[data-ka-count-keys-word]'),
      countKeysNote: q('[data-ka-count-keys-note]'),
      countKeyIcons: q('[data-ka-count-key-icons]'),
    };
    const MAX_ICONS = 12;
    const keysIncluded = parseInt(data.keysIncluded, 10) || 0;

    const state = { type: null, finish: null, internal: null, external: null };
    let items = [];
    let extraKeys = 0;
    let busy = false;

    const saved = store.read();
    if (saved && Array.isArray(saved.items)) {
      items = saved.items.filter((item) => {
        const cylinder = cylinders.get(item.block);
        const variant = cylinder && variantById(cylinder, item.variantId);
        return variant && variant.available;
      }).slice(0, MAX_ITEMS);
      extraKeys = clamp(saved.extraKeys, 0, MAX_KEYS);
    }

    const save = () => {
      if (items.length || extraKeys) store.write({ items, extraKeys });
      else store.clear();
    };

    const currentVariant = () => {
      const cylinder = cylinders.get(state.type);
      if (!cylinder) return null;
      if (!state.internal || !state.external) return null;
      return findVariant(cylinder, state.finish, state.internal, state.external) || null;
    };

    const chip = (group, value, checked, disabled, extra) => {
      const id = `${group}-${value.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
      const wrap = make('div', 'upvc-ka__chip');
      const input = make('input', 'upvc-ka__radio');
      input.type = 'radio';
      input.name = group;
      input.id = id;
      input.value = value;
      input.checked = checked;
      input.disabled = disabled;
      const label = make('label', 'upvc-ka__chip-label');
      label.htmlFor = id;
      if (extra) label.append(extra);
      label.append(make('span', 'upvc-ka__chip-text', value));
      if (disabled) label.append(make('span', 'upvc-ka__chip-note', 'Sold out'));
      wrap.append(input, label);
      return { wrap, input };
    };

    const renderFinishes = () => {
      const cylinder = cylinders.get(state.type);
      els.finishes.innerHTML = '';
      els.finishStep.hidden = !cylinder || cylinder.finishIdx < 0;
      if (!cylinder || cylinder.finishIdx < 0) return;
      cylinder.finishes.forEach((finish) => {
        const available = cylinder.variants.some(
          (variant) => variant.available && variant.options[cylinder.finishIdx] === finish,
        );
        const swatch = make('span', 'upvc-ka__swatch');
        swatch.setAttribute('aria-hidden', 'true');
        const key = finishKey(finish);
        if (key) swatch.style.background = SWATCHES[key];
        const { wrap, input } = chip(`ka-finish-${root.id}`, finish, finish === state.finish, !available, swatch);
        input.addEventListener('change', () => {
          state.finish = finish;
          const variant = currentVariant();
          if (!variant || !variant.available) state.external = null;
          renderSizes();
          renderPreview();
        });
        els.finishes.append(wrap);
      });
    };

    const renderSizes = () => {
      const cylinder = cylinders.get(state.type);
      els.sizes.innerHTML = '';
      els.sizeStep.hidden = !cylinder;
      if (!cylinder) return;
      ['internal', 'external'].forEach((side) => {
        const wrap = make('div', 'upvc-ka__size-field');
        const id = `ka-${side}-${root.id}`;
        const title = side === 'internal' ? 'Internal size' : 'External size';
        const detail = cylinder.thumbturn ? (side === 'internal' ? 'thumbturn side' : 'key side') : (side === 'internal' ? 'inside' : 'outside');
        const label = make('label', '', `${title} (${detail})`);
        label.htmlFor = id;
        const select = make('select', 'upvc-ka__size-select');
        select.id = id;
        select.name = side;
        const placeholder = make('option', '', `Choose ${title.toLowerCase()}`);
        placeholder.value = '';
        select.append(placeholder);
        cylinder[`${side}Sizes`].forEach((size) => {
          const option = make('option', '', size);
          option.value = size;
          option.disabled = !cylinder.variants.some((variant) => variant.available && variant[side] === size && (cylinder.finishIdx < 0 || variant.options[cylinder.finishIdx] === state.finish) && (side !== 'external' || !state.internal || variant.internal === state.internal));
          select.append(option);
        });
        select.value = state[side] || '';
        select.addEventListener('change', () => {
          state[side] = select.value || null;
          if (side === 'internal') {
            const variant = currentVariant();
            if (!variant || !variant.available) state.external = null;
            renderSizes();
            els.sizes.querySelector('[name="internal"]').focus();
          }
          renderPreview();
        });
        wrap.append(label, select);
        els.sizes.append(wrap);
      });
    };

    const renderPreview = () => {
      const cylinder = cylinders.get(state.type);
      if (!cylinder) return;
      const variant = currentVariant();
      els.previewImage.src = artFor(cylinder, state.finish);
      els.previewTitle.textContent = cylinder.label;
      if (variant) {
        els.previewDetail.textContent = describe(cylinder, variant);
        els.previewPrice.textContent = money(variant.price);
      } else {
        els.previewDetail.textContent = [state.finish, 'choose internal and external sizes'].filter(Boolean).join(' · ');
        els.previewPrice.textContent = `From ${money(cylinder.minPrice)}`;
      }
      const full = items.length >= MAX_ITEMS;
      els.add.disabled = !variant || !variant.available || full;
      els.add.textContent = full ? 'Your set is full' : 'Add to my set';
    };

    const selectType = (block) => {
      const cylinder = cylinders.get(block);
      if (!cylinder) return;
      state.type = block;
      if (cylinder.finishIdx >= 0 && !cylinder.finishes.includes(state.finish)) {
        state.finish =
          cylinder.finishes.find((finish) =>
            cylinder.variants.some((variant) => variant.available && variant.options[cylinder.finishIdx] === finish),
          ) || cylinder.finishes[0];
      }
      state.internal = null;
      state.external = null;
      els.types.forEach((input) => {
        input.checked = input.value === block;
      });
      renderFinishes();
      renderSizes();
      renderPreview();
    };

    const chargesFor = (count) => {
      if (count < 2) return 0;
      return data.rule === 'each' ? count : count - 1;
    };

    const totals = () => {
      const cylinderTotal = items.reduce((sum, item) => {
        const variant = variantById(cylinders.get(item.block), item.variantId);
        return sum + (variant ? variant.price : 0);
      }, 0);
      const charges = chargesFor(items.length);
      const chargeTotal = data.charge ? charges * data.charge.price : 0;
      const keyTotal = data.extraKey ? extraKeys * data.extraKey.price : 0;
      return { cylinderTotal, charges, chargeTotal, keyTotal, total: cylinderTotal + chargeTotal + keyTotal };
    };

    // Picture of the set: one render per cylinder and one key per key.
    const renderCount = () => {
      const count = items.length;
      els.count.hidden = count === 0;
      if (!count) return;
      els.countCyl.textContent = count;
      els.countCylWord.textContent = count === 1 ? 'euro cylinder' : 'euro cylinders';
      els.countCylIcons.innerHTML = '';
      items.slice(0, MAX_ICONS).forEach((item) => {
        const cylinder = cylinders.get(item.block);
        const variant = variantById(cylinder, item.variantId);
        const img = make('img', 'upvc-ka__count-cylinder');
        img.src = artFor(cylinder, cylinder.finishIdx >= 0 ? variant.options[cylinder.finishIdx] : '');
        img.alt = '';
        img.width = 72;
        img.height = 51;
        els.countCylIcons.append(img);
      });
      if (count > MAX_ICONS) els.countCylIcons.append(make('span', 'upvc-ka__count-more', `+${count - MAX_ICONS}`));

      const totalKeys = keysIncluded + extraKeys;
      els.countKeys.textContent = totalKeys;
      els.countKeysWord.textContent = totalKeys === 1 ? 'key' : 'keys';
      els.countKeysNote.textContent = extraKeys
        ? `${keysIncluded} included + ${extraKeys} extra`
        : `${keysIncluded} included with the set`;
      els.countKeyIcons.innerHTML = '';
      for (let i = 0; i < Math.min(totalKeys, MAX_ICONS); i += 1) {
        const extra = i >= keysIncluded;
        const wrap = make('span', `upvc-ka__count-key${extra ? ' upvc-ka__count-key--extra' : ''}`);
        const img = make('img');
        img.src = data.keyImage;
        img.alt = '';
        img.width = 19;
        img.height = 56;
        wrap.append(img);
        els.countKeyIcons.append(wrap);
      }
      if (totalKeys > MAX_ICONS) els.countKeyIcons.append(make('span', 'upvc-ka__count-more', `+${totalKeys - MAX_ICONS}`));
    };

    const renderSet = () => {
      els.list.innerHTML = '';
      items.forEach((item, index) => {
        const cylinder = cylinders.get(item.block);
        const variant = variantById(cylinder, item.variantId);
        const finish = cylinder.finishIdx >= 0 ? variant.options[cylinder.finishIdx] : '';
        const detail = describe(cylinder, variant);
        const row = make('li', 'upvc-ka__item');
        const thumb = make('img', 'upvc-ka__item-image');
        thumb.src = artFor(cylinder, finish);
        thumb.alt = '';
        thumb.width = 120;
        thumb.height = 86;
        const text = make('div', 'upvc-ka__item-text');
        text.append(make('strong', '', `${index + 1}. ${cylinder.label}`));
        text.append(make('span', '', detail));
        if (item.door) text.append(make('span', 'upvc-ka__item-door', item.door));
        const side = make('div', 'upvc-ka__item-side');
        side.append(make('span', 'upvc-ka__item-price', money(variant.price)));
        const actions = make('div', 'upvc-ka__item-actions');
        const copy = make('button', 'upvc-ka__link', 'Copy');
        copy.type = 'button';
        copy.setAttribute('aria-label', `Add another ${cylinder.label}, ${detail}`);
        copy.disabled = items.length >= MAX_ITEMS;
        copy.addEventListener('click', () => {
          if (items.length >= MAX_ITEMS) return;
          items.splice(index + 1, 0, { ...item, uid: `${Date.now()}-${Math.random()}`, door: '' });
          renderSet();
          renderPreview();
        });
        const remove = make('button', 'upvc-ka__link upvc-ka__link--remove', 'Remove');
        remove.type = 'button';
        remove.setAttribute('aria-label', `Remove cylinder ${index + 1}, ${cylinder.label}, ${detail}`);
        remove.addEventListener('click', () => {
          items.splice(index, 1);
          renderSet();
          renderPreview();
          const next = els.list.querySelectorAll('.upvc-ka__link--remove')[Math.min(index, items.length - 1)];
          (next || els.add).focus();
        });
        actions.append(copy, remove);
        side.append(actions);
        row.append(thumb, text, side);
        els.list.append(row);
      });

      const count = items.length;
      const sums = totals();
      els.empty.hidden = count > 0;
      els.summary.hidden = count === 0;
      els.cylLabel.textContent = `Cylinders × ${count}`;
      els.cylTotal.textContent = money(sums.cylinderTotal);
      els.chargeLabel.textContent = `Keyed alike charges × ${sums.charges}`;
      if (data.charge) {
        const each = `${money(data.charge.price)} each`;
        els.chargeNote.textContent =
          data.rule === 'each' ? each : `${each}. The first cylinder sets the key, so it has no charge.`;
      } else {
        els.chargeNote.textContent = '';
      }
      els.chargeTotal.textContent = money(sums.chargeTotal);
      els.keysRow.hidden = !data.extraKey;
      if (data.extraKey) els.keyPrice.textContent = `${money(data.extraKey.price)} each`;
      els.keys.value = extraKeys;
      els.total.textContent = money(sums.total);

      let hint = '';
      if (!data.charge && count) hint = 'Keyed alike is not available online right now. Please message us to order.';
      else if (count === 1) hint = 'Add 1 more cylinder to key them alike.';
      else if (count > 1) hint = `${plural(count, 'cylinder', 'cylinders')}, one key.`;
      els.hint.textContent = hint;

      els.submit.disabled = busy || count < 2 || !data.charge;
      els.submit.textContent = count >= 2 ? `Add set to basket · ${money(sums.total)}` : 'Add set to basket';

      els.bar.hidden = count === 0;
      els.barText.textContent = `${plural(count, 'cylinder', 'cylinders')} · ${plural(keysIncluded + extraKeys, 'key', 'keys')} · ${money(sums.total)}`;
      renderCount();
      save();
    };

    const announce = (node, message, isError) => {
      node.classList.toggle('is-error', Boolean(isError));
      node.textContent = message;
    };

    els.types.forEach((input) => {
      input.addEventListener('change', () => selectType(input.value));
    });

    els.add.addEventListener('click', () => {
      const cylinder = cylinders.get(state.type);
      const variant = currentVariant();
      if (!cylinder || !variant || !variant.available || items.length >= MAX_ITEMS) return;
      const door = els.door.value.trim().slice(0, 40);
      items.push({ uid: `${Date.now()}-${Math.random()}`, block: cylinder.block, variantId: variant.id, door });
      els.door.value = '';
      renderSet();
      renderPreview();
      announce(
        els.addStatus,
        `Added ${cylinder.label}, ${describe(cylinder, variant)}${door ? ` for the ${door}` : ''}. Your set has ${plural(items.length, 'cylinder', 'cylinders')}.`,
      );
      els.submitStatus.textContent = '';
    });

    const setKeys = (value) => {
      extraKeys = clamp(value, 0, MAX_KEYS);
      renderSet();
    };
    root.querySelectorAll('[data-ka-keys-step]').forEach((button) => {
      button.addEventListener('click', () => setKeys(extraKeys + parseInt(button.dataset.kaKeysStep, 10)));
    });
    els.keys.addEventListener('change', () => setKeys(els.keys.value));

    const reset = () => {
      items = [];
      extraKeys = 0;
      renderSet();
      renderPreview();
      els.addStatus.textContent = '';
    };

    els.submit.addEventListener('click', () => {
      if (busy || items.length < 2 || !data.charge) return;
      const ref = newRef();
      const grouped = new Map();
      items.forEach((item) => {
        const key = `${item.variantId}|${item.door}`;
        const line = grouped.get(key) || { id: item.variantId, quantity: 0, door: item.door };
        line.quantity += 1;
        grouped.set(key, line);
      });
      const payload = [...grouped.values()].map((line) => {
        const properties = { 'Keyed alike set': ref };
        if (line.door) properties.Door = line.door;
        return { id: line.id, quantity: line.quantity, properties };
      });
      const sums = totals();
      if (sums.charges) payload.push({ id: data.charge.id, quantity: sums.charges, properties: { 'Keyed alike set': ref } });
      if (data.extraKey && extraKeys) {
        payload.push({ id: data.extraKey.id, quantity: extraKeys, properties: { 'Keyed alike set': ref } });
      }

      busy = true;
      els.submit.disabled = true;
      els.submit.setAttribute('aria-busy', 'true');
      announce(els.submitStatus, 'Adding your set to the basket…');

      fetch(`${root.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: payload }),
      })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) throw new Error(body.description || body.message || 'Your set could not be added.');
          return fetch(`${root.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null);
        })
        .then((cart) => {
          if (cart) window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
          const count = items.length;
          busy = false;
          els.submit.removeAttribute('aria-busy');
          if (root.dataset.redirect === 'true') {
            store.clear();
            window.location.href = root.dataset.cartUrl;
            return;
          }
          reset();
          els.submitStatus.classList.remove('is-error');
          els.submitStatus.innerHTML = '';
          els.submitStatus.append(
            make('strong', '', `Set ${ref} is in your basket.`),
            ` ${plural(count, 'cylinder', 'cylinders')} will be keyed alike. `,
          );
          const link = make('a', 'upvc-ka__status-link', 'Go to basket');
          link.href = root.dataset.cartUrl;
          els.submitStatus.append(link, ' or build another set above.');
        })
        .catch((error) => {
          busy = false;
          els.submit.removeAttribute('aria-busy');
          renderSet();
          announce(els.submitStatus, `${error.message} Please try again or message us.`, true);
        });
    });

    els.fallback.hidden = true;
    els.app.hidden = false;
    const firstType = els.types[0];
    if (firstType) selectType(firstType.value);
    renderSet();
  };

  const run = (scope = document) => scope.querySelectorAll('[data-ka-builder]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run());
  else run();

  document.addEventListener('shopify:section:load', (event) => run(event.target));
})();
